import { HubConnectionState } from "@microsoft/signalr";
import { useUnsavedChanges } from "contexts/UnsavedChangesContextProvider";
import { CommentReadDto } from "functions/api/comments/interface";
import { SearchableEditorBlocks } from "functions/api/enums";
import { TlfLoaderData } from "functions/api/tlfItem/tlfLoader";
import { isEqual } from "lodash";
import { startTransition, useContext, useEffect, useEffectEvent, useMemo, useState } from "react";
import { useLoaderData, useSearchParams } from "react-router-dom";
import { useAppSettings } from "../AppSettingsContext";
import { SignalRContext } from "../SignalRProvider";
import { HubGroupMessage, TlfUpdateActionSignal } from "../signalr/interface";
import { SAVING_TOC_ITEM_VIA_TLF } from "../toc/interface";
import { getTlfUrl } from "./getTlfUrl";
import {
  ADD_RECEIVED_MESSAGE,
  CLEAR_TLF,
  LOAD_TLF,
  PATCH_TLF_COMMENTS,
  PROCESS_SIGNALR_NOTIFICATION,
  REMOVE_TLF_COMMENT,
  SET_EDITOR_BLOCKS_SEARCH_PANEL,
  SET_TLF_READ_ONLY,
  SET_VIEW_MODE,
  UPDATE_TLF_COMMENT,
} from "./interface";
import { loadTlfViewModeFromStorage } from "./tlfViewModes";

/**
 * Runs route-scoped TLF effects against the unified AppSettings context.
 *
 * @param readOnly - Whether the active route must prevent TLF modifications.
 */
export const useTlfEffects = (readOnly = false): void => {
  const loaderData = useLoaderData() as TlfLoaderData;
  const tlfData = loaderData?.tlfItem;
  const isSubscribed = loaderData?.isTlfSubscribed ?? false;
  const isTlfNew = loaderData?.isTlfNew ?? false;
  const [searchParams] = useSearchParams();
  const { tlfState, hierarchyState, tocState, dispatch } = useAppSettings();
  const hierarchy = hierarchyState.hierarchy;
  const signalR = useContext(SignalRContext);
  const { navigate, registerUnsavedChanges } = useUnsavedChanges("tlf-context");

  useEffect(() => {
    const savedViewMode = loadTlfViewModeFromStorage();
    if (savedViewMode.value !== tlfState.tlfViewMode?.value) {
      dispatch({
        operation: SET_VIEW_MODE,
        calledFrom: "useTlfEffects:InitializeViewMode",
        tlfViewMode: savedViewMode,
      });
    }
  }, [dispatch, tlfState.tlfViewMode?.value]);

  useEffect(() => {
    // Route effects run only when loader data differs from the current TLF state.
    /* istanbul ignore else */
    if (tlfData && !isEqual(tlfData, tlfState.tlfData)) {
      /* istanbul ignore else */
      if (
        tlfData.id !== tlfState.tlfData?.id ||
        (!tlfState.changed &&
          !tlfState.forcePushToParent &&
          !isEqual(tlfData, tlfState.savedTlfData))
      ) {
        dispatch({
          operation: LOAD_TLF,
          calledFrom: "useTlfEffects:Initial",
          tlfData,
          isSubscribed,
          isTlfNew,
          readOnly,
        });
      }
    }
  }, [dispatch, isSubscribed, isTlfNew, readOnly, tlfState, tlfData]);

  useEffect(() => {
    if (tlfState.readOnly !== readOnly) {
      dispatch({ operation: SET_TLF_READ_ONLY, calledFrom: "useTlfEffects:ReadOnly", readOnly });
    }
  }, [dispatch, readOnly, tlfState.readOnly]);

  const signalRGroup = useMemo(
    () => (tlfState.tlfData ? `tlf${tlfState.tlfData.id}` : null),
    [tlfState.tlfData],
  );
  const [lastSignalRGroupRequest, setLastSignalRGroupRequest] = useState<string>("");
  const sendGroupRequest = useEffectEvent(async (group: string) => {
    signalR?.connectToGroup(group);
    startTransition(() => setLastSignalRGroupRequest(group));
  });
  useEffect(() => {
    // SignalR group requests are made only after the provider reports a connected group.
    /* istanbul ignore else */
    if (
      signalR?.connection?.state === HubConnectionState.Connected &&
      signalRGroup &&
      lastSignalRGroupRequest !== signalRGroup
    ) {
      sendGroupRequest(signalRGroup);
    }
  }, [lastSignalRGroupRequest, signalR?.connection?.state, signalRGroup]);

  useEffect(() => {
    /* istanbul ignore else */
    if (
      hierarchy &&
      tlfState.tlfData &&
      searchParams.has("searchText") &&
      searchParams.has("blocksToSearch")
    ) {
      const searchText = searchParams.get("searchText")!;
      const blocksToSearch = searchParams
        .getAll("blocksToSearch")
        .filter((label) =>
          Object.values(SearchableEditorBlocks).includes(label as SearchableEditorBlocks),
        )
        .map((label) => label as SearchableEditorBlocks);
      const editorBlocksSearchPanelStatus = {
        searchText,
        blocksToSearch,
        showSearchPanel: true,
      };
      if (!isEqual(editorBlocksSearchPanelStatus, tlfState.editorBlocksSearchPanelStatus)) {
        dispatch({
          operation: SET_EDITOR_BLOCKS_SEARCH_PANEL,
          calledFrom: "useTlfEffects:UrlSearchEditorText",
          editorBlocksSearchPanelStatus,
        });
        navigate(getTlfUrl(hierarchy, tlfState.tlfData), { replace: true });
      }
    }
  }, [
    dispatch,
    hierarchy,
    navigate,
    searchParams,
    tlfState.editorBlocksSearchPanelStatus,
    tlfState.tlfData,
  ]);

  useEffect(() => {
    /**
     * Processes a SignalR group message for the current TLF.
     *
     * @param message - Serialised SignalR message containing a TLF update or comment event.
     */
    const updateFromSignalR = (message: string) => {
      const tlfUpdate = JSON.parse(message) as HubGroupMessage;
      const tlfUpdatePatch =
        tlfUpdate.text && tlfUpdate.text[0].startsWith("{")
          ? (JSON.parse(tlfUpdate.text[0]) as TlfUpdateActionSignal)
          : undefined;
      /* istanbul ignore else */
      if (
        tlfUpdatePatch &&
        tlfState.tlfData?.id === tlfUpdatePatch.updatedItemId &&
        tlfUpdate.displayName !== signalR?.connection?.connectionId &&
        tlfUpdate.displayName !== "Backend" &&
        tlfUpdate.messageType === "tlfUpdate" &&
        !tlfState.receivedMessages.some((message) => message.messageId === tlfUpdate.messageId)
      ) {
        dispatch({
          operation: ADD_RECEIVED_MESSAGE,
          calledFrom: "SignalR:TlfUpdate",
          receivedMessage: tlfUpdate,
        });
        dispatch({
          operation: PROCESS_SIGNALR_NOTIFICATION,
          calledFrom: "SignalR",
          patch: tlfUpdatePatch.patch,
        });
        // Comment updates are only received for the active TLF and its comment collection.
        /* istanbul ignore else */
      } else if (
        tlfUpdate.groupName === `tlf${tlfState.tlfData?.id}` &&
        tlfUpdate.displayName !== signalR?.connection?.connectionId &&
        tlfUpdate.displayName !== "Backend" &&
        tlfUpdate.text &&
        tlfState.tlfData?.comments
      ) {
        switch (tlfUpdate.messageType) {
          case "deleteComment": {
            try {
              const commentId = JSON.parse(tlfUpdate.text[0]).id;
              /* istanbul ignore else -- valid delete-comment payloads always include an ID. */
              if (commentId)
                dispatch({
                  operation: REMOVE_TLF_COMMENT,
                  commentId,
                  calledFrom: "SignalR:DeleteComment",
                });
            } catch (error) {
              console.warn("Error processing delete comment update:", error);
            }
            break;
          }
          case "comment": {
            try {
              const comment = JSON.parse(tlfUpdate.text[0]) as CommentReadDto;
              /* istanbul ignore else -- valid comment payloads always contain a comment object. */
              if (comment)
                dispatch({ operation: UPDATE_TLF_COMMENT, comment, calledFrom: "SignalR:Comment" });
            } catch (error) {
              console.warn("Error processing comment update:", error);
            }
            break;
          }
          case "commentsPatch": {
            try {
              const patch = JSON.parse(tlfUpdate.text[0]);
              /* istanbul ignore else -- valid patch payloads always contain a patch value. */
              if (patch)
                dispatch({
                  operation: PATCH_TLF_COMMENTS,
                  calledFrom: "SignalR:CommentsPatch",
                  patch,
                });
            } catch (error) {
              console.warn("Error processing comments patch update:", error);
            }
            break;
          }
        }
      }
    };
    // The listener is registered only while a TLF item is active.
    /* istanbul ignore else */
    if (tlfState.tlfData) {
      signalR?.connection?.on("SendMessageToGroup", updateFromSignalR);
      return () => signalR?.connection?.off("SendMessageToGroup");
    }
  }, [dispatch, signalR?.connection, tlfState.receivedMessages, tlfState.tlfData]);

  useEffect(() => {
    if (tlfState.requestStatus.requesting && tocState.blocked !== "patching") {
      dispatch({ operation: SAVING_TOC_ITEM_VIA_TLF });
    }
  }, [dispatch, tocState.blocked, tlfState.requestStatus]);

  useEffect(() => {
    registerUnsavedChanges("TLF", tlfState.changed || tlfState.delayedHierarchyUpdates);
    return () => {
      registerUnsavedChanges("TLF", false);
    };
  }, [dispatch, registerUnsavedChanges, tlfState.changed, tlfState.delayedHierarchyUpdates]);

  /** Unmount if the component is being removed from the DOM. */
  useEffect(() => {
    return () => {
      dispatch({ operation: CLEAR_TLF, calledFrom: "useTlfEffects:Unmount" });
    };
  }, [dispatch]);
};
